import {callAPI} from 'services/api';
import {CurrencyAmount} from 'util/currency';
import {DateTime, TimeSpan} from 'util/time';
import {
  Booking,
  BookingField,
  BookingInsuranceOptions,
  BookingProduct,
  BookingService,
  BookingSettings,
  BookingsFilters,
  BookingTimes,
  BookingTimesFilters,
  DraftBooking,
  ExpandedBooking,
  NewBooking
} from 'services/entities/booking';
import {readSpace, readSpaceAnalyticsGroup, readSpaceAnalyticsValues} from 'services/spaces';
import {readDesign} from 'services/designs';
import {getTimePrice, loadSettings, loadSpacePricingCalendar} from 'services/pricing';
import {Product, ProductOptionCode, ProductSKU} from 'services/entities/products';
import Cookies from 'js-cookie';
import {formatQueryString} from 'util/url';
import {loadSpaceProducts} from 'services/products';
import {loadDiscounts, readDiscount} from 'services/discounts';
import {Discount} from 'services/entities/discounts';
import {applyCurrencyTaxes, applyDiscounts} from 'util/pricing';
import config from 'config';
import {readCurrency} from 'services/currency';
import {readUser} from "services/auth";


type AllBookingTypes = NewBooking|Booking|ExpandedBooking;

function readBookingProduct(data: any): BookingProduct {
  return { sku: data.sku, quantity: data.quantity, optionValues: data.option_values };
}

function readBookingOccupationalLicenseURL(url: string): URL|null {
  if(!url) {
    return null;
  }
  return new URL(url, config.api.url);
}

export function readBooking(data: any): Booking {
  const { creator, created, start_time, end_time, products, discounts, occupation_license, currency, daily_analytics,
    aggregated_analytics, ...props } = data;
  return {
    creator: readUser(creator),
    created: DateTime.parse(created),
    startTime: DateTime.parse(start_time),
    endTime: DateTime.parse(end_time),
    products: products.map(readBookingProduct),
    discounts: discounts.map(readDiscount),
    currency: readCurrency(currency),
    occupationalLicenseURL: readBookingOccupationalLicenseURL(occupation_license),
    dailyAnalytics: daily_analytics ? daily_analytics.map(readSpaceAnalyticsGroup) : null,
    aggregatedAnalytics: aggregated_analytics ? readSpaceAnalyticsValues(aggregated_analytics) : null,
    ...props
  } as Booking;
}

function readExpandedBooking(data: any): ExpandedBooking {
  const { creator, space, design, start_time, end_time, products, discounts, outstanding_amount, outstanding_due_date,
    currency, occupation_license, ...props } = data;
  const bookingCurrency = readCurrency(currency);
  return {
    creator: readUser(creator),
    startTime: DateTime.parse(start_time),
    endTime: DateTime.parse(end_time),
    products: products.map(readBookingProduct),
    discounts: discounts.map(readDiscount),
    space: readSpace(space),
    design: design ? readDesign(design) : null,
    occupationalLicenseURL: readBookingOccupationalLicenseURL(occupation_license),
    outstandingAmount: outstanding_amount ? CurrencyAmount.parse(outstanding_amount, bookingCurrency) : null,
    outstandingDueDate: outstanding_due_date ? DateTime.parse(outstanding_due_date) : null,
    ...props
  } as ExpandedBooking;
}

function readBookingTimes(data: any): BookingTimes {
  return {
    start: DateTime.parse(data.start_time),
    end: DateTime.parse(data.end_time),
  };
}

export async function createBooking(booking: NewBooking, paymentMethodID: string): Promise<Booking[]> {
  const response = await callAPI('/bookings/', 'POST', {
    booking: {
      space: booking.space,
      design: booking.design,
      times: booking.times.map(t => { return { start: t.startTime.toISOString(), end: t.endTime.toISOString(),
        include_flexi_ticket: t.includeFlexiTicket } }),
      products: booking.products.filter(pr => pr.quantity > 0).map(pr => {
        return { sku: pr.sku, quantity: pr.quantity, option_values: pr.optionValues}
      }),
      discounts: booking.discounts
    },
    payment_method: paymentMethodID,
    tracking_id: Cookies.get('hubspotutk')
  }, true);
  return response.map(readBooking);
}

export async function updateBooking(code: string, products: BookingProduct[]): Promise<Booking> {
  const response = await callAPI(`/bookings/${code}/`, 'PUT', {
    products: products.filter(pr => pr.quantity > 0).map(pr => {
      return { sku: pr.sku, quantity: pr.quantity, option_values: pr.optionValues}
    })
  }, true);
  return readBooking(response);
}

export async function updateBookingDesign(bookingCode: string, designCode: string): Promise<Booking> {
  const response = await callAPI(`/bookings/${bookingCode}/design/`, 'PUT', {design: designCode}, true);
  return readBooking(response);
}

export async function updateBookingInsurance(bookingCode: string, options:BookingInsuranceOptions): Promise<any> {
  const trackingID = Cookies.get('hubspotutk');
  const formData = {
    type: options.type,
    food_drink: options.foodDrink,
    has_insurance: options.hasInsurance,
    tracking_id: trackingID
  };
  await callAPI(`/bookings/${bookingCode}/insurance/`, 'PUT', formData, true);
}

export async function updateBookingServices(bookingCode: string, services:BookingService[]): Promise<any> {
  const trackingID = Cookies.get('hubspotutk');
  const formData = {
    services: services.map(s => s === BookingService.BrandAssets ? 'brand_assets' : s),
    tracking_id: trackingID
  };
  await callAPI(`/bookings/${bookingCode}/services/`, 'PUT', formData, true);
}

export async function loadBooking(code: string): Promise<Booking> {
  const response = await callAPI(`/bookings/${code}/`, 'GET', null, true);
  return readBooking(response);
}

export function addQueryFilters(queryParams: {[index: string]: string}, filters: BookingsFilters) {
    if(filters.minTime) {
      queryParams['min_time'] = filters.minTime.format();
    }
    if(filters.maxTime) {
      queryParams['max_time'] = filters.maxTime.format();
    }
    if(filters.space) {
      queryParams['space'] = filters.space;
    }
    if(filters.design) {
      queryParams['design'] = filters.design;
    }
}

export async function loadBookings(filters?: BookingsFilters, fields?: BookingField[]): Promise<Booking[]> {
  const queryParams: {[index: string]: string} = {};
  if(fields) {
    queryParams['fields'] = fields.join(',');
  }
  if(filters) {
    addQueryFilters(queryParams, filters);
  }
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/?${queryString}`, 'GET', null, true);
  return response.map(readBooking);
}

export async function loadExpandedBookings(filters?: BookingsFilters): Promise<ExpandedBooking[]> {
  const queryParams: {[index: string]: string} = {expand: 'space,design', fields: [BookingField.OutstandingAmount, BookingField.OutstandingDueDate].join(',')};
  if(filters) {
    addQueryFilters(queryParams, filters);
  }
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/?${queryString}`, 'GET', null, true);
  return response.map(readExpandedBooking);
}

export async function loadBookingTimes(filters?:BookingTimesFilters): Promise<{start: DateTime, end: DateTime}[]> {
  const queryParams: {[index: string]: string} = {};
  if(filters) {
    if(filters.minTime) {
      queryParams['min_time'] = filters.minTime.format()
    }
    if(filters.maxTime) {
      queryParams['max_time'] = filters.maxTime.format();
    }
    if(filters.ignoreCode) {
      queryParams['ignore_code'] = filters.ignoreCode;
    }
    if(filters.space) {
      queryParams['space'] = filters.space;
    }
  }
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/times/?${queryString}`, 'GET', null, true);
  return response.map(readBookingTimes);
}

export async function loadBookingProductPrice(booking: AllBookingTypes, product: Product): Promise<CurrencyAmount|null> {
  const bookingProduct = booking.products.find(bp => bp.sku === product.sku);
  if(!bookingProduct || bookingProduct.quantity <= 0) {
    return null;
  }
  if(product.sku === ProductSKU.FlexiTicket) {
    return loadBookingFlexiTicketPrice(booking);
  } else {
    const localPrice = product.prices.find(pr => pr.currency!.isDefault);
    return localPrice!.multiply(getBookingProductQuantityMultiplier(booking, product.sku) * bookingProduct.quantity);
  }
}

export async function loadBookingFlexiTicketPrice(booking: AllBookingTypes, includeTaxes :boolean = false): Promise<CurrencyAmount|null> {
  if('times' in booking) {
    let price: CurrencyAmount|null = null;
    for(const t of booking.times) {
      if(t.includeFlexiTicket) {
        const timePrice = await loadFlexiTicketPrice(booking, t.startTime, t.endTime, includeTaxes);
        if(timePrice) {
          price = price ? price.add(timePrice) : timePrice;
        }
      }
    }
    return price;
  } else if(booking.products.find(pr => pr.sku === ProductSKU.FlexiTicket)) {
    return await loadFlexiTicketPrice(booking, booking.startTime, booking.endTime, includeTaxes);
  } else {
    return null;
  }
}

export async function loadFlexiTicketPrice(booking: AllBookingTypes, startTime: DateTime, endTime: DateTime, includeTaxes :boolean = false): Promise<CurrencyAmount|null> {
  const settings = await loadSettings();
  let bookingPrice = await loadBookingTimeRentalPrice(booking, startTime, endTime);
  if(!bookingPrice) {
    return null;
  }
  bookingPrice = bookingPrice.multiply(settings.flexiTicketRate);
  if(includeTaxes) {
    bookingPrice = await applyCurrencyTaxes(bookingPrice);
  }
  return bookingPrice;
}

export function getBookingProductQuantityMultiplier(booking: AllBookingTypes, sku: string): number {
  const bookingProduct = booking.products.find((bp: BookingProduct) => bp.sku === sku);
  if(!bookingProduct) {
    return 0;
  }
  switch(sku) {
    case ProductSKU.Staff:
      let durationHours = getBookingDurationHours(booking);
      if(bookingProduct.optionValues[ProductOptionCode.HoursBefore]) {
        durationHours++;
      }
      if(bookingProduct.optionValues[ProductOptionCode.HoursAfter]) {
        durationHours++;
      }
      return durationHours;
    case ProductSKU.StorageBoxes:
      let days = 1;
      const daysAfterValue = bookingProduct.optionValues[ProductOptionCode.DaysAfter];
      if(daysAfterValue) {
        days += parseInt(daysAfterValue);
      }
      const daysBeforeValue = bookingProduct.optionValues[ProductOptionCode.DaysBefore];
      if(daysBeforeValue) {
        days += parseInt(daysBeforeValue);
      }
      return days;
    default:
      return getBookingDurationHours(booking);
  }
}

function getBookingDurationHours(booking: AllBookingTypes): number {
  if('times' in booking) {
    let hours = 0;
    for(const t of booking.times) {
      hours += t.endTime.diff(t.startTime).totalHours;
    }
    return hours;
  } else {
    return booking.endTime.diff(booking.startTime).totalHours;
  }
}

export async function loadBookingRentalPrice(booking: AllBookingTypes,
                                             includeTaxes: boolean = false, includeDiscounts: boolean = false): Promise<CurrencyAmount|null> {
  if(!booking.space || ('times' in booking && booking.times.length === 0)) {
    return null;
  }
  if('times' in booking) {
    let price = null;
    for(const t of booking.times) {
      const timePrice = await loadBookingTimeRentalPrice(booking, t.startTime, t.endTime, includeTaxes, includeDiscounts);
      if(timePrice) {
        price = price ? price.add(timePrice) : timePrice;
      }
    }
    return price;
  } else {
    return await loadBookingTimeRentalPrice(booking, booking.startTime, booking.endTime, includeTaxes, includeDiscounts);
  }
}

export async function loadBookingTimeRentalPrice(booking: AllBookingTypes, start: DateTime, end: DateTime,
                                             includeTaxes: boolean = false, includeDiscounts: boolean = false): Promise<CurrencyAmount|null> {
  if(!booking.space) {
    return null;
  }
  const date = start;
  const spaceCode = typeof(booking.space) === 'string' ? booking.space : booking.space.code;
  const calendar = await loadSpacePricingCalendar(spaceCode, date);
  if(!calendar) {
    return null;
  }
  const settings = await loadSettings();
  let dt = start;
  let price = null;
  while(dt.lessThan(end)) {
    const incrementPrice = getTimePrice(dt, calendar);
    if(incrementPrice) {
      price = price ? price.add(incrementPrice) : incrementPrice;
    }
    dt = dt.add(settings.increment);
  }
  if(price) {
    price = price.multiply(settings.increment.totalSeconds / settings.priceDuration.totalSeconds);
    if(includeDiscounts) {
      price = await applyDiscounts(price, await loadDiscounts(booking.discounts));
    }
    if(includeTaxes) {
      price = await applyCurrencyTaxes(price);
    }
  }
  return price;
}

export async function loadBookingInitialChargeAmount(booking: AllBookingTypes): Promise<CurrencyAmount|null> {
  let price = await loadBookingRentalPrice(booking);
  if(price) {
    const flexiTicketPrice = await loadBookingFlexiTicketPrice(booking);
    if(flexiTicketPrice) {
      price = price.add(flexiTicketPrice);
    }
    price = await applyDiscounts(price, await loadDiscounts(booking.discounts));
    price = await applyCurrencyTaxes(price);
  }
  return price;
}

export async function loadBookingProductsPrice(booking: AllBookingTypes, includeTaxes: boolean = false,
                                               includeDiscounts: boolean = false): Promise<CurrencyAmount|null> {
  let price: CurrencyAmount|null = null;
  const spaceCode = typeof(booking.space) === 'string' ? booking.space : booking.space.code;
  const products = await loadSpaceProducts(spaceCode);
  for (const product of booking.products) {
    const pr = products.find(p => p.sku === product.sku);
    if(!pr) {
      throw new Error("Product not found: " + product.sku);
    }
    const productPrice = await loadBookingProductPrice(booking, pr);
    if(productPrice) {
      price = price ? price.add(productPrice) : productPrice;
    }
  }
  if('times' in booking) {
    for(const t of booking.times) {
      if(t.includeFlexiTicket) {
        let flexiTicketPrice = await loadFlexiTicketPrice(booking, t.startTime, t.endTime);
        if(flexiTicketPrice) {
          price = price ? price.add(flexiTicketPrice) : flexiTicketPrice;
        }
      }
    }
  }
  if(price) {
    if(includeDiscounts) {
      price = await applyDiscounts(price, await loadDiscounts(booking.discounts));
    }
    if(includeTaxes) {
      price = await applyCurrencyTaxes(price);
    }
  }
  return price;
}

export async function loadBookingPrice(booking: AllBookingTypes, includeTax: boolean = false,
                                       includeDiscounts: boolean = false): Promise<CurrencyAmount|null> {
  let price = await loadBookingRentalPrice(booking, includeTax, includeDiscounts);
  if(!price) {
    return null;
  }
  const productsPrice = await loadBookingProductsPrice(booking, includeTax, includeDiscounts);
  if(productsPrice) {
    price = price.add(productsPrice);
  }
  return price;
}

function getBookingStartTime(booking: DraftBooking|Booking): DateTime|null {
  if('times' in booking) {
    return booking.times.length > 0 ? booking.times[0].startTime : null;
  } else {
    return booking.startTime;
  }
}

export async function loadBookingFinalPaymentDate(booking: DraftBooking|Booking): Promise<DateTime|null> {
  if(!booking.products || !booking.products.find(pr => pr.sku !== ProductSKU.SpaceRental && pr.sku !== ProductSKU.FlexiTicket && pr.quantity > 0)) {
    // booking has no add-on products - no final payment
    return null;
  }
  const startTime = getBookingStartTime(booking);
  if(!startTime) {
    return null;
  }
  const settings = await loadSettings();
  let finalPaymentDate: DateTime|null = startTime.subtract(new TimeSpan({days: settings!.finalPaymentDateOffset}));
  if (finalPaymentDate.lessThan(DateTime.today(settings!.defaultTimeZone))) {
    // final payment date has passed
    finalPaymentDate = null;
  }
  return finalPaymentDate;
}

export function bookingAddOnsAreEditable(booking: DraftBooking|Booking, settings: BookingSettings): boolean {
  const startTime = getBookingStartTime(booking);
  if(!startTime) {
    return false;
  }
  const maxEditableTime = startTime.date.subtract(new TimeSpan({days: settings!.finalPaymentDateOffset}));
  return DateTime.today(settings!.defaultTimeZone).lessThan(maxEditableTime);
}

export function bookingCanCheckOut(booking: DraftBooking|Booking): boolean {
  return !('times' in booking) || booking.times.length > 0;
}

export async function loadDiscountAmount(booking: DraftBooking|Booking, discount:Discount,
                                         includeTaxes: boolean = false): Promise<CurrencyAmount|null> {
  const totalPrice = await loadBookingPrice(booking, includeTaxes, false);
  return totalPrice ? totalPrice.multiply(discount.rate) : null;
}

export async function loadBookingOccupationLicense(code: string): Promise<Blob> {
  return await callAPI(`/bookings/${code}/occupational-license/`, 'GET', null, true);
}

export async function loadBookingInvoice(code: string): Promise<Blob> {
  return await callAPI(`/bookings/${code}/invoice/`, 'GET', null, true);
}
